/**
 * dashboard domain (doc 01 A1.1 / A2.1 / A5.1). All read-only. Types mirror the
 * API's `dashboard/schemas.ts` interfaces verbatim (mark for consolidation).
 *
 * Every summary carries an optional `staleness` banner; screens render a
 * StalenessNote inside the affected figure and link it to `/records` (doc 07 §7.6).
 */
import { del, get, post, put } from './transport.js';
/** `GET /api/dashboard/config` */
export function config(options) {
    return get('/api/dashboard/config', options);
}
/** `GET /api/dashboard/service-levels` */
export function serviceLevels(query = {}, options) {
    return get('/api/dashboard/service-levels', {
        ...options,
        query: { ...query },
    });
}
/** `GET /api/dashboard/service-levels/workers` */
export function serviceLevelWorkers(
/**
 * `period` is accepted and does not size anything: every row carries both the
 * 7-day and the 30-day window. There is no benchmark parameter here on
 * purpose — a uniform bar can never be the plan behind a statement about a
 * named worker.
 */
query = {}, options) {
    return get('/api/dashboard/service-levels/workers', {
        ...options,
        query: { ...query },
    });
}
/** `GET /api/dashboard/care-gaps` */
export function careGaps(query = {}, options) {
    return get('/api/dashboard/care-gaps', { ...options, query: { ...query } });
}
/** `GET /api/dashboard/care-gaps/clients` */
export function careGapDetails(query = {}, options) {
    return get('/api/dashboard/care-gaps/clients', {
        ...options,
        query: { ...query },
    });
}
/** `GET /api/dashboard/documentation` */
export function documentation(query = {}, options) {
    return get('/api/dashboard/documentation', {
        ...options,
        query: { ...query },
    });
}
/** `GET /api/dashboard/documentation/open` */
export function documentationDetails(query = {}, options) {
    return get('/api/dashboard/documentation/open', {
        ...options,
        query: { ...query },
    });
}
/** `GET /api/dashboard/reconciliation` */
export function reconciliation(query = {}, options) {
    return get('/api/dashboard/reconciliation', {
        ...options,
        query: { ...query },
    });
}
/** `GET /api/dashboard/reconciliation/items` */
export function reconciliationDetails(query = {}, options) {
    return get('/api/dashboard/reconciliation/items', {
        ...options,
        query: { ...query },
    });
}
/** `GET /api/dashboard/exceptions` */
export function exceptions(query = {}, options) {
    return get('/api/dashboard/exceptions', { ...options, query: { ...query } });
}
/** `GET /api/dashboard/exceptions/detail` */
export function exceptionDetails(options) {
    return get('/api/dashboard/exceptions/detail', options);
}
/** `GET /api/dashboard/integration-health` */
export function integrationHealth(options) {
    return get('/api/dashboard/integration-health', options);
}
/** `GET /api/dashboard/analytics/filter-options` */
export function analyticsFilterOptions(options) {
    return get('/api/dashboard/analytics/filter-options', options);
}
/** `GET /api/dashboard/analytics/overview` */
export function analyticsOverview(query = {}, options) {
    return get('/api/dashboard/analytics/overview', {
        ...options,
        query: { ...query },
    });
}
/**
 * `GET /api/dashboard/analytics/trends`. Pass the period picker's `preset`
 * (and `from`/`to`) to follow it; with no `preset` the axis is the trailing
 * 26 weeks. Either way it starts where the organization's records begin.
 */
export function analyticsTrends(query = {}, options) {
    return get('/api/dashboard/analytics/trends', {
        ...options,
        query: { ...query },
    });
}
/**
 * `GET /api/dashboard/analytics/staff`
 *
 * Returns `StaffSummary` to a `dashboard:read` caller and `StaffAggregate` to
 * an `analytics:read`-only one. Branch on `disclosure.level` (always present),
 * not on the presence of `byWorker`.
 */
export function analyticsStaff(query = {}, options) {
    return get('/api/dashboard/analytics/staff', { ...options, query: { ...query } });
}
/**
 * `GET /api/dashboard/analytics/schedule`
 *
 * Returns `ScheduleSummary` to a `dashboard:read` caller and
 * `ScheduleAggregate` to an `analytics:read`-only one.
 */
export function analyticsSchedule(query = {}, options) {
    return get('/api/dashboard/analytics/schedule', {
        ...options,
        query: { ...query },
    });
}
/** `GET /api/dashboard/analytics/clients` */
export function analyticsClients(query = {}, options) {
    return get('/api/dashboard/analytics/clients', {
        ...options,
        query: { ...query },
    });
}
/** `GET /api/dashboard/analytics/care-gaps/trend` */
export function analyticsCareGapsTrend(query = {}, options) {
    return get('/api/dashboard/analytics/care-gaps/trend', {
        ...options,
        query: { ...query },
    });
}
/** `GET /api/dashboard/analytics/documentation/trend` */
export function analyticsDocumentationTrend(query = {}, options) {
    return get('/api/dashboard/analytics/documentation/trend', {
        ...options,
        query: { ...query },
    });
}
/** `GET /api/dashboard/analytics/impact` */
export function analyticsImpact(query = {}, options) {
    return get('/api/dashboard/analytics/impact', {
        ...options,
        query: { ...query },
    });
}
/** `GET /api/dashboard/metrics` */
export function metricCatalog(options) {
    return get('/api/dashboard/metrics', options);
}
/** `GET /api/dashboard/templates` */
export function dashboardTemplates(options) {
    return get('/api/dashboard/templates', options);
}
/** `GET /api/dashboard/layouts` */
export function dashboardLayouts(options) {
    return get('/api/dashboard/layouts', options);
}
/** `GET /api/dashboard/layouts/:id` */
export function dashboardLayout(id, options) {
    return get(`/api/dashboard/layouts/${id}`, options);
}
/** `POST /api/dashboard/layouts` */
export function createDashboardLayout(body, options) {
    return post('/api/dashboard/layouts', body, options);
}
/** `PUT /api/dashboard/layouts/:id` */
export function updateDashboardLayout(id, body, options) {
    return put(`/api/dashboard/layouts/${id}`, body, options);
}
/** `POST /api/dashboard/layouts/:id/set-default` */
export function setDefaultDashboardLayout(id, options) {
    return post(`/api/dashboard/layouts/${id}/set-default`, undefined, options);
}
/** `DELETE /api/dashboard/layouts/:id` */
export function deleteDashboardLayout(id, options) {
    return del(`/api/dashboard/layouts/${id}`, options);
}
/** `GET /api/dashboard/widgets/:metricKey` */
export function dashboardWidgetData(metricKey, query = {}, options) {
    return get(`/api/dashboard/widgets/${metricKey}`, {
        ...options,
        query: { ...query },
    });
}
//# sourceMappingURL=dashboard.js.map